import { V3Pill } from "@/components/OpsSectionHead";
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Loader2, RefreshCw, ChevronDown } from "lucide-react";
import { InfoTip } from "./InfoTip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/exec-v2`;
const LINKS = { churn: ["/ops?tab=churn", "Churn"], focus: ["/follow-up-queue", "Focus: Texts"], renewals: ["/ops?tab=package-renewals", "Package Renewals"],
  booking: ["/ops", "Booking curve"], leads: ["/follow-up-queue", "Follow-Up Queue"] };
const Md = ({ t }) => <span dangerouslySetInnerHTML={{ __html: String(t || "").replace(/[<>&]/g, "").replace(/\*\*(.+?)\*\*/g, "<b>$1</b>") }} />;
const Pill = V3Pill;

const Col = ({ title, items, tone, tid }) => (
  <div className={`rounded-xl p-3 space-y-2 ${tone}`} data-testid={tid}>
    <div className="text-[11px] font-extrabold text-gray-500 tracking-wide">{title}</div>
    {(items || []).map((t, i) => <div key={i} className="text-sm text-gray-800"><Md t={t} /></div>)}
  </div>
);

const monday = () => { const d = new Date(); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return d.toDateString(); };
const OPEN_KEY = "execBriefingOpen";
const initialOpen = () => { try { const s = JSON.parse(localStorage.getItem(OPEN_KEY) || "null"); if (s?.week === monday()) return s.open; } catch { /* ignore */ } return new Date().getDay() === 1; };

// Monday Morning Briefing: AI-written weekly; auto-open Mondays, collapsible, remembers choice per week.
export const MondayBriefing = () => {
  const [b, setB] = useState(null);
  const [open, setOpen] = useState(initialOpen);
  const toggle = () => setOpen((o) => { localStorage.setItem(OPEN_KEY, JSON.stringify({ week: monday(), open: !o })); return !o; });
  const [busy, setBusy] = useState(false);
  const load = (refresh = false) => {
    setBusy(true);
    return axios.get(`${API}/briefing${refresh ? "?refresh=true" : ""}`).then((r) => setB(r.data))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Briefing unavailable"); setB((x) => x || {}); }).finally(() => setBusy(false));
  };
  useEffect(() => {
    const onRefresh = () => load();
    load();
    window.addEventListener("exec:refresh", onRefresh);
    return () => window.removeEventListener("exec:refresh", onRefresh);
  }, []);
  return (
    <Card className="border-0 shadow-md p-4 sm:p-5" data-testid="exec-briefing">
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={toggle} className="inline-flex items-center gap-2" data-testid="exec-briefing-toggle" aria-expanded={open}>
          <Pill>Monday Morning Briefing</Pill>
          <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </button>
        <span className="text-xs text-gray-400">{b?.week ? `Week of ${b.week} · AI-written from your Gingr data` : "AI-written weekly from your Gingr data"}</span>
        {open && <button onClick={() => load(true)} disabled={busy} className="ml-auto text-xs border rounded-lg px-2 py-1 text-gray-500 inline-flex items-center gap-1" data-testid="exec-briefing-refresh">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}Refresh now
        </button>}
      </div>
      {!open ? (b?.back?.[0] && <button onClick={toggle} className="block w-full text-left text-sm text-gray-700 mt-2 truncate hover:text-gray-900" data-testid="exec-briefing-preview"><Md t={b.back[0]} /> <span className="text-xs font-bold text-indigo-600">· Show full briefing</span></button>)
      : !b ? <div className="text-sm text-gray-400 flex items-center gap-2 py-6"><Loader2 className="w-4 h-4 animate-spin" />Writing this week's briefing…</div> : (
        <div className="grid md:grid-cols-3 gap-3 mt-3" data-testid="exec-briefing-body">
          <Col title="◀ LAST WEEK — LOOKING BACK" items={b.back} tone="bg-gray-50" tid="exec-briefing-back" />
          <Col title="▶ NEXT 2 WEEKS — LOOKING AHEAD" items={b.ahead} tone="bg-gray-50" tid="exec-briefing-ahead" />
          <div className="rounded-xl p-3 space-y-2 bg-emerald-50 border border-emerald-100" data-testid="exec-briefing-grow">
            <div className="text-[11px] font-extrabold text-emerald-700 tracking-wide">★ GROW THE BUSINESS — DO THIS WEEK</div>
            {(b.grow || []).map((g, i) => { const l = LINKS[g.link]; return (
              <div key={i} className="text-sm text-gray-800">{i + 1}. <Md t={g.text} />{l && <Link to={l[0]} className="ml-1 text-[11px] font-bold text-indigo-600 hover:underline">→ {l[1]}</Link>}</div>); })}
          </div>
        </div>
      )}
    </Card>
  );
};

const CHIP = { up: "bg-emerald-50 text-emerald-700 border-emerald-200", down: "bg-amber-50 text-amber-700 border-amber-200", hold: "bg-gray-50 text-gray-600 border-gray-200" };
const Soon = ({ title, children }) => (
  <div className="rounded-xl border border-dashed border-gray-300 p-3">
    <div className="flex items-center gap-1.5 text-[11px] font-extrabold text-gray-500">{title}<span className="text-[9px] font-extrabold bg-gray-200 text-gray-600 rounded-full px-1.5 py-0.5">COMING SOON</span></div>
    <div className="text-[11px] text-gray-500 mt-1">{children}</div>
  </div>
);

// Staffing Planner: next 7 days of booked dogs → staff needed vs that weekday's normal.
export const StaffingPlanner = () => {
  const [d, setD] = useState(null);
  useEffect(() => {
    let t;
    const load = (refresh) => { clearTimeout(t); return axios.get(`${API}/staffing${refresh === true ? "?refresh=true" : ""}`)
      .then((r) => { setD(r.data); t = setTimeout(load, r.data.status === "computing" || r.data.refreshing ? 8000 : 180000); })
      .catch(() => { setD((x) => x || { days: [] }); t = setTimeout(load, 180000); }); };
    const onRefresh = () => load(true);
    load();
    window.addEventListener("exec:refresh", onRefresh);
    return () => { clearTimeout(t); window.removeEventListener("exec:refresh", onRefresh); };
  }, []);
  const r = d?.rules || {};
  return (
    <Card className="border-0 shadow-md p-4 sm:p-5" data-testid="exec-staffing">
      <div className="flex flex-wrap items-center gap-3">
        <Pill>Staffing Planner</Pill>
        <InfoTip title="Staffing Planner" tid="info-staffing">
          <p>For each of the next 7 days we count the dogs booked in Gingr (daycare + boarders in playgroups, split Large/Small) and baths, then apply your ratios:</p>
          <p>• 1 play-care tech (PCT) per {r.large_per_pct || 25} large dogs and per {r.small_per_pct || 30} small dogs<br />• 1 bather per ~{r.baths_per_bather || 17.5} baths (×{r.large_bath_factor || 1.5} when mostly large-dog baths), +1 when {r.bath_rush || 6}+ bath dogs go home within 2 hours<br />• fixed {r.csr || 2} front desk (CSR) + {r.mgr || 1} manager + {r.floaters || 1} floater ({r.floaters_busy || 2} when over {r.busy_dogs || 80} dogs)</p>
          <p><b>Normal</b> = the same math on the same weekday over the last 4 weeks. ▲/▼ = how many more/fewer staff than normal, and which role drives it.</p>
          <p className="text-gray-500">Future days fill up as bookings come in. Updates in the background every 30 minutes.</p>
        </InfoTip>
        <span className="text-[10px] font-extrabold bg-amber-100 text-amber-800 rounded-full px-2 py-0.5" data-testid="exec-staffing-indev">IN DEVELOPMENT</span>
        <span className="text-xs text-gray-400">scale staff to the dogs, not the calendar · dogs booked so far</span>
      </div>
      <div className="grid lg:grid-cols-4 gap-4 mt-3">
        <div className="lg:col-span-3 space-y-2 min-w-0">
          <div className="text-[11px] font-extrabold text-gray-500 tracking-wide">▶ NEXT 7 DAYS — WHERE TO SCALE UP / DOWN</div>
          {!d || d.status === "computing" ? <div className="text-sm text-gray-400 flex items-center gap-2 py-6"><Loader2 className="w-4 h-4 animate-spin" />Reading the next 7 days from Gingr…</div> : (
            <div className="overflow-x-auto"><table className="w-full text-xs" data-testid="exec-staffing-table">
              <thead><tr className="text-[10px] text-gray-400 font-bold border-b">{["Day", "Large", "Small", "Baths", "PCT Lg", "PCT Sm", "Bathers", "Fixed", "Total", "Normal", "Suggestion"].map((h) => <td key={h} className="py-1 pr-2 whitespace-nowrap">{h.toUpperCase()}</td>)}</tr></thead>
              <tbody>{d.days.map((x) => (
                <tr key={x.date} className="border-b last:border-0" data-testid={`exec-staffing-row-${x.date}`}>
                  <td className="py-1.5 pr-2 font-bold whitespace-nowrap">{x.label}</td><td>{x.large}</td><td>{x.small}</td>
                  <td className="whitespace-nowrap">{x.baths}{x.bath_rush >= (r.bath_rush || 6) && <span className="text-gray-400"> · {x.bath_rush} out ~{x.bath_rush_at}</span>}</td>
                  <td>{x.pct_large}</td><td>{x.pct_small}</td><td>{x.bathers}</td><td className="text-gray-500">{x.fixed}</td>
                  <td className="font-bold">{x.total}</td><td className="text-gray-400">{Math.round(x.normal * 2) / 2}</td>
                  <td><span className={`border rounded-full px-2 font-bold whitespace-nowrap ${CHIP[x.suggestion?.kind] || CHIP.hold}`}>{x.suggestion?.label}</span></td>
                </tr>))}
                {!d.days.length && <tr><td colSpan={11} className="py-6 text-center text-gray-400">No bookings found for the next 7 days.</td></tr>}
              </tbody>
            </table></div>
          )}
          <div className="text-[11px] text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
            <b>Rules:</b> 1 PCT per {r.large_per_pct || 25} large · 1 PCT per {r.small_per_pct || 30} small · 1 bather per ~{r.baths_per_bather || 17.5} baths (×{r.large_bath_factor || 1.5} when large-dog baths dominate) · +1 bather when {r.bath_rush || 6}+ bath dogs go home within 2 hrs · fixed {r.csr || 2} CSR + {r.mgr || 1} manager + {r.floaters || 1}–{r.floaters_busy || 2} floaters. Dogs = daycare + boarders in playgroups (Gingr Large/Small flags). Normal = same weekday, last 4 weeks. Future days fill up as bookings come in.
          </div>
        </div>
        <div className="space-y-3">
          <Soon title="◀ LABOR %">Labor $ ÷ revenue with trend vs your target.</Soon>
          <Soon title="SCHEDULED VS SUGGESTED">Connect <b>When I Work</b> to compare scheduled staff with these suggestions.</Soon>
          <Soon title="$ PER DOG-DAY">Labor $ per dog-day and revenue per staff hour.</Soon>
          <Soon title="EDIT RATIOS">Set your own ratios per location.</Soon>
        </div>
      </div>
    </Card>
  );
};
